<div class="header-label">
  <h2>SFTP Migration Configuration</h2>
</div>

<!-- Filter row -->
<div class="top-controls-row">
  <mat-form-field
    appearance="outline"
    class="search-bar"
    subscriptSizing="dynamic"
  >
    <mat-label>Search Client</mat-label>
    <input
      matInput
      [value]="searchTerm"
      (input)="onSearch($any($event.target).value)"
      placeholder="Type to search..."
      autocomplete="off"
    />
    <button
      *ngIf="searchTerm"
      matSuffix
      mat-icon-button
      type="button"
      aria-label="Clear"
      (click)="clearSearch()"
    >
      <mat-icon>close</mat-icon>
    </button>
    <mat-icon matPrefix>search</mat-icon>
  </mat-form-field>

  <button
    mat-flat-button
    type="button"
    color="primary"
    class="top-controls-action-btn add-config-btn"
    matTooltip="Add Configuration"
    aria-label="Add Configuration"
    (click)="openAdd()"
  >
    +
  </button>
</div>

<!-- Error banner -->
<div *ngIf="errorMessage" class="api-error-banner">
  {{ errorMessage }}
</div>

<!-- Loading -->
<div class="table-loader" *ngIf="loading">
  <mat-spinner [diameter]="50"></mat-spinner>
</div>

<!-- Table -->
<div class="table-wrapper" [class.loading]="loading">
  <table
    mat-table
    [dataSource]="dataSource"
    matSort
    [matSortActive]="sortBy"
    [matSortDirection]="sortDir"
  >
    <ng-container matColumnDef="client">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="client">Client</th>
      <td mat-cell *matCellDef="let row">{{ row.client }}</td>
    </ng-container>

    <ng-container matColumnDef="sourcePrefix">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="sourcePrefix">
        Source Path
      </th>
      <td
        mat-cell
        *matCellDef="let row"
        class="path-cell"
        [matTooltip]="row.sourcePrefix"
      >
        {{ row.sourcePrefix }}
      </td>
    </ng-container>

    <ng-container matColumnDef="destinationPrefix">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="destinationPrefix">
        Destination Path
      </th>
      <td
        mat-cell
        *matCellDef="let row"
        class="path-cell"
        [matTooltip]="row.destinationPrefix"
      >
        {{ row.destinationPrefix }}
      </td>
    </ng-container>

    <ng-container matColumnDef="enabled">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="enabled">
        Enabled
      </th>
      <td mat-cell *matCellDef="let row">
        <mat-icon class="status-icon" [class.active]="row.enabled">
          {{ row.enabled ? "check_circle" : "cancel" }}
        </mat-icon>
      </td>
    </ng-container>

    <ng-container matColumnDef="service">
      <th mat-header-cell *matHeaderCellDef mat-sort-header="service">
        Service
      </th>
      <td mat-cell *matCellDef="let row">{{ row.service }}</td>
    </ng-container>

    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef class="no-header"></th>
      <td mat-cell *matCellDef="let row" class="actions-cell">
        <button
          mat-icon-button
          matTooltip="Edit"
          class="table-action-btn table-action-edit"
          (click)="openEdit(row); $event.stopPropagation()"
        >
          <mat-icon>edit</mat-icon>
        </button>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
  </table>

  <div
    *ngIf="dataSource.data.length === 0 && !loading"
    class="no-results-message"
  >
    No configurations found
  </div>
</div>

<!-- Paginator -->
<mat-paginator
  [length]="totalCount"
  [pageIndex]="pageIndex"
  [pageSize]="pageSize"
  [pageSizeOptions]="[10, 20, 50]"
  (page)="onPageChange($event)"
  showFirstLastButtons
>
</mat-paginator>

<!-- Add / Edit dialog -->
<ng-template #configDialog>
  <h2 mat-dialog-title>
    {{ isEditMode ? "Edit Configuration" : "Add Configuration" }}
  </h2>

  <form #configForm="ngForm" (ngSubmit)="save(configForm)">
    <mat-dialog-content class="dialog-content">
      <div *ngIf="dialogError" class="api-error-banner dialog-error">
        {{ dialogError }}
      </div>

      <div class="dialog-fields">
        <mat-form-field appearance="outline" class="dialog-field">
          <mat-label>Client</mat-label>
          <input
            matInput
            type="text"
            placeholder="Search client..."
            [formControl]="clientControl"
            [matAutocomplete]="clientAuto"
            [disabled]="isEditMode"
            #clientTrigger="matAutocompleteTrigger"
            (input)="onClientInput()"
          />
          <app-autocomplete-suffix-actions
            matSuffix
            [showClear]="
              !isEditMode &&
              clientControl.value !== null &&
              clientControl.value !== ''
            "
            [toggleDisabled]="isEditMode"
            [panelOpen]="clientTrigger.panelOpen"
            (clearClicked)="clearClient($event)"
            (toggleClicked)="openOrClosePanel($event, clientTrigger)"
          >
          </app-autocomplete-suffix-actions>
          <mat-autocomplete
            #clientAuto="matAutocomplete"
            [clearStaleSelectionOnOpen]="clientControl.value"
            [displayWith]="displayClient"
            (optionSelected)="onClientSelected($event.option.value)"
          >
            <mat-option
              *ngFor="let client of filteredClients$ | async"
              [value]="client"
            >
              {{ client.DisplayName }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <mat-form-field appearance="outline" class="dialog-field">
          <mat-label>Source Path</mat-label>
          <input
            matInput
            name="sourcePrefix"
            [(ngModel)]="model.sourcePrefix"
            required
            #sourcePrefixField="ngModel"
          />
          <mat-error *ngIf="sourcePrefixField.hasError('required')"
            >Source Path is required.</mat-error
          >
        </mat-form-field>

        <mat-form-field appearance="outline" class="dialog-field">
          <mat-label>Destination Path</mat-label>
          <input
            matInput
            name="destinationPrefix"
            [(ngModel)]="model.destinationPrefix"
            required
            #destinationPrefixField="ngModel"
          />
          <mat-error *ngIf="destinationPrefixField.hasError('required')"
            >Destination Path is required.</mat-error
          >
        </mat-form-field>

        <mat-form-field appearance="outline" class="dialog-field">
          <mat-label>Service</mat-label>
          <mat-select
            name="service"
            [(ngModel)]="model.service"
            required
            #serviceField="ngModel"
          >
            <mat-option
              *ngFor="let service of serviceOptions"
              [value]="service"
            >
              {{ service }}
            </mat-option>
          </mat-select>
          <mat-error *ngIf="serviceField.hasError('required')"
            >Service is required.</mat-error
          >
        </mat-form-field>
      </div>

      <div class="dialog-flags">
        <mat-checkbox name="enabled" [(ngModel)]="model.enabled"
          >Enabled</mat-checkbox
        >
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" type="submit">Submit</button>
      <button mat-stroked-button type="button" (click)="closeDialog()">
        Cancel
      </button>
    </mat-dialog-actions>
  </form>
</ng-template>
